"use client";

import { Button, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { CreateDemoRequestPayload } from "@/_services/postRequestForm";
import { InputField } from "@/_components/ui/form/InputField";
import { TextareaField } from "@/_components/ui/form/TextareaField";
import { useAuthStore } from "@/_stores/useAuthStore";
import { useTranslations } from "next-intl";
import { IoSend } from "react-icons/io5";
import { KnootLottie } from "@/_components/ui/lotties/Lottie";

type DemoRequestFormProps = {
  onSuccess?: () => void;
};

type SubmitState = "idle" | "success" | "error";

type DemoFormValues = {
  name: string;
  email: string;
  phone?: string | null;
  effortLevel: string[];
  priority: string[];
  dueDate?: string | null;
  description?: string | null;
};

const schema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup.string().optional().nullable(),
  effortLevel: yup.array(yup.string().required()).min(1).required(),
  priority: yup.array(yup.string().required()).min(1).required(),
  dueDate: yup.string().optional().nullable(),
  description: yup.string().optional().nullable(),
});

export function DemoRequestForm({ onSuccess }: DemoRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const userEmail = useAuthStore((state) => state.user?.userInfo?.email || "");
  const t = useTranslations("settings.demo_request");
  const methods = useForm<DemoFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: userEmail,
      phone: "",
      effortLevel: ["Medium"],
      priority: ["Medium"],
      dueDate: "",
      description: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    setIsSubmitting(true);
    setApiError(null);
    setSubmitState("idle");

    try {
      const payload: CreateDemoRequestPayload = {
        name: values.name,
        email: values.email,
        phone: values.phone ? values.phone : undefined,
        effortLevel: values.effortLevel?.[0],
        priority: values.priority?.[0],
        dueDate: values.dueDate ? values.dueDate : undefined,
        description: values.description ? values.description : undefined,
      };

      const res = await fetch("/api/notion/post-request-form/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to submit request");
      }

      setSubmitState("success");
    } catch (e) {
      setApiError(e instanceof Error ? e.message : String(e));
      setSubmitState("error");
    } finally {
      setIsSubmitting(false);
    }
  });

  if (submitState === "success") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <KnootLottie
          animationName="completed"
          loop={false}
          height="200px"
          width="200px"
        />
        <Text
          fontWeight="light"
          textStyle="lg"
          fontFamily="heading"
          textAlign="center"
        >
          {t("sussess_message")}
        </Text>
        <Button
          onClick={() => onSuccess?.()}
          loading={isSubmitting}
          alignSelf="flex-end"
        >
          Close
        </Button>
      </VStack>
    );
  }

  if (submitState === "error") {
    return (
      <VStack align="stretch" gap={4} pt={2}>
        <Text fontWeight="semibold" color="red.500">
          Submit failed
        </Text>
        {apiError ? <Text color="red.500">{apiError}</Text> : null}
        <HStack justify="flex-end">
          <Button
            onClick={() => {
              setSubmitState("idle");
              setApiError(null);
            }}
            variant="outline"
          >
            Try again
          </Button>
          <Button
            onClick={() => onSuccess?.()}
            loading={isSubmitting}
            alignSelf="flex-end"
          >
            Close
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <FormProvider {...methods}>
      <VStack
        as="form"
        align="stretch"
        gap={4}
        onSubmit={onSubmit}
        h="fit-content"
        id="demo-request-form"
      >
        <Text>{t("message")}</Text>
        <InputField
          name="name"
          label={t("name")}
          isFloating={true}
          isRequired={true}
        />

        <InputField
          name="email"
          label={t("email")}
          isFloating={true}
          isRequired={true}
          type="email"
          isDisabled={!!userEmail}
        />

        <InputField
          name="phone"
          label={t("phone")}
          isFloating={true}
          isDisabled={false}
        />

        <TextareaField
          name="description"
          label={t("description")}
          isFloating={true}
          rows={4}
        />

        {apiError && <Text color="red.500">{apiError}</Text>}

        <Button size="lg" type="submit" loading={isSubmitting}>
          <HStack gap={2}>
            {t("submit")}
            <Icon as={IoSend} boxSize={4} />
          </HStack>
        </Button>
      </VStack>
    </FormProvider>
  );
}
